
import React, { useState, useEffect } from "react";
import { Modal } from "../../../components/Modal";
import { FileUploadKit } from "../../../components/common/FileUploadKit";
import { uploadApprovalFile, createApprovalRequest } from "../../../services/approvalRequestsService"; 
import { createOrResetApprovalFlow } from "../../../services/approvalFlowService";
import { useAuth } from "../../../context/AuthContext";
import { useToast } from "../../../components/Toast";
import { generateDocCode } from "../../../utils/idGenerator";
import { getDeptCodeByEmail } from "../../../config/roles";
import { getDeptNameByCode } from "../../../constants/departments";
import { ApprovalModule } from "../../../types/legalOps";

interface Props {
  onClose: () => void;
  onSuccess: () => void;
}

export function SealRequestFormModal({ onClose, onSuccess }: Props) {
  const { user } = useAuth();
  const { showToast } = useToast();
  
  const [form, setForm] = useState({
    title: "",
    description: "", // Maps to "Purpose"
    category: "一般用印",
    copyCount: 1,
    sealType: "大小章",
    department: "",
    requesterName: "",
    docCode: ""
  });
  
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
     if (user) {
         const deptCode = getDeptCodeByEmail(user.email);
         const deptName = getDeptNameByCode(deptCode);
         const newCode = generateDocCode("seal", new Date(), []);
         setForm(prev => ({
             ...prev,
             department: deptName,
             requesterName: user.displayName || user.email,
             docCode: newCode
         }));
     }
  }, [user]);

  const handleSave = async (submitNow: boolean) => {
    if (!form.title) { alert("請填寫文件標題"); return; }
    if (!form.description) { alert("請填寫用印事由"); return; }
    
    setIsSubmitting(true);
    try {
        const payload: any = {
             module: 'SEAL_REQUEST' as ApprovalModule, // Explicitly set standard module key
             title: form.title,
             description: form.description, 
             docCode: form.docCode,
             docCategory: form.category,
             department: form.department,
             requesterName: form.requesterName,
             requesterUid: user?.id || '',
             priority: 'normal',
             sensitivity: 'normal',
             status: submitNow ? 'IN_REVIEW' : 'DRAFT',
             
             // Restore Legacy Fields
             sealType: form.sealType,
             copyCount: Number(form.copyCount),
             attachments: []
        };

        const docId = await createApprovalRequest(payload);

        // Handle Pending Files
        if (pendingFiles.length > 0 && docId) {
             const newAtts = [];
             for(const f of pendingFiles) {
                 const m = await uploadApprovalFile(docId, 'request', f, user?.email||'');
                 newAtts.push(m);
             }
             // Update with attachments
             const { updateApprovalRequest } = await import("../../../services/approvalRequestsService");
             await updateApprovalRequest(docId, { attachments: newAtts });
        }

        if (submitNow && docId) {
             await createOrResetApprovalFlow(
                 "SEAL_REQUEST",
                 docId,
                 user?.id || "",
                 form.department,
                 form.requesterName
             );
             showToast("用印申請已送出 (進入簽核)");
        } else {
             showToast("用印申請已暫存");
        }
        
        onSuccess();
        onClose();
    } catch(e) {
        console.error(e);
        alert("儲存失敗");
    } finally {
        setIsSubmitting(false);
    }
  };

  return (
    <Modal title="新增用印申請" onClose={onClose} width={700}>
      <div className="grid">
         <div className="field">
            <label className="label">申請單號 (自動)</label>
            <input className="input" value={form.docCode} readOnly style={{background:'rgba(255,255,255,0.05)', color:'#fbbf24', fontFamily:'monospace'}} />
         </div>
         <div className="field">
            <label className="label">申請部門</label>
            <input className="input" value={form.department} readOnly style={{background:'#222', color:'#aaa'}} />
         </div>
         
         <div className="field" style={{gridColumn:"1/-1"}}>
            <label className="label">文件標題 <span style={{color:'red'}}>*</span></label>
            <input className="input" value={form.title} onChange={e=>setForm({...form, title:e.target.value})} placeholder="例如：113年度經銷合約用印" />
         </div>
         
         <div className="field">
            <label className="label">印鑑類型 (Seal Type)</label>
            <select className="input" value={form.sealType} onChange={e=>setForm({...form, sealType:e.target.value})}>
                <option value="大小章">大小章</option>
                <option value="公司章">公司章 (僅大章)</option>
                <option value="負責人章">負責人章 (僅小章)</option>
                <option value="發票章">發票章</option>
                <option value="合約章">合約章</option>
                <option value="其他">其他</option>
            </select>
         </div>
         
         <div className="field">
            <label className="label">用印份數</label>
            <input type="number" min={1} className="input" value={form.copyCount} onChange={e=>setForm({...form, copyCount:Number(e.target.value)})} />
         </div>

         <div className="field" style={{gridColumn:"1/-1"}}>
            <label className="label">用印事由 / 用途說明 <span style={{color:'red'}}>*</span></label>
            <textarea 
                className="input" 
                rows={4} 
                value={form.description} 
                onChange={e=>setForm({...form, description:e.target.value})} 
                placeholder="請說明用印目的、文件內容摘要、對象..."
            />
         </div>
      </div>
      
      <FileUploadKit 
        label="上傳用印文件"
        rawFiles={pendingFiles}
        onFilesSelected={fs => setPendingFiles(p => [...p, ...fs])} 
        onRemoveRawFile={(idx) => setPendingFiles(prev => prev.filter((_, i) => i !== idx))}
      />
      
      <div className="row" style={{marginTop:20, justifyContent:'flex-end', gap: 10}}>
         <button className="btn" style={{width:'auto', background:'transparent', border:'1px solid #555', color:'#ccc'}} onClick={() => handleSave(false)} disabled={isSubmitting}>
             暫存 (Draft)
         </button>
         <button className="btn" style={{width:'auto'}} onClick={() => handleSave(true)} disabled={isSubmitting}>
             存檔並送出呈核
         </button>
      </div>
    </Modal>
  );
}
